import { toast } from 'sonner';
import type { MapsApp } from './MapsAppSelectionDialog';

export interface NavigationDestination {
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
}

/**
 * Detects the current platform for navigation URL schemes
 */
export function getPlatform(): 'ios' | 'android' | 'web' {
  if (typeof navigator === 'undefined') return 'web';

  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
    return 'ios';
  } else if (userAgent.includes('android')) {
    return 'android';
  }

  return 'web';
}

/**
 * Checks if a maps app is likely available on the current platform
 */
export function isMapsAppAvailable(app: MapsApp, platform: 'ios' | 'android' | 'web' = getPlatform()): boolean {
  switch (app) {
    case 'google':
      return platform !== 'web'; // Google Maps app available on mobile
    case 'apple':
      return platform === 'ios'; // Apple Maps only on iOS
    case 'waze':
      return platform !== 'web'; // Waze available on mobile
    case 'system':
      return true; // System default always available
    default:
      return false;
  }
}

/**
 * Generates navigation URLs for different maps apps and platforms
 */
export function generateMapsUrl(
  destination: NavigationDestination,
  app: MapsApp,
  platform: 'ios' | 'android' | 'web' = getPlatform()
): string {
  const { latitude, longitude, name, address } = destination;
  const coords = `${latitude},${longitude}`;
  const query = name || address || coords;

  switch (app) {
    case 'google':
      if (platform === 'ios') {
        // iOS Google Maps URL scheme
        return `comgooglemaps://?daddr=${coords}&directionsmode=driving&q=${encodeURIComponent(query)}`;
      } else if (platform === 'android') {
        // Android Google Maps intent
        return `geo:${coords}?q=${encodeURIComponent(query)}`;
      }
      // Web fallback
      return `https://www.google.com/maps/dir/?api=1&destination=${coords}&travelmode=driving`;

    case 'apple':
      if (platform === 'ios') {
        // iOS Apple Maps URL scheme
        return `maps:///?daddr=${coords}&dirflg=d&q=${encodeURIComponent(query)}`;
      }
      // Fallback for other platforms
      return `https://maps.apple.com/?daddr=${coords}&dirflg=d`;

    case 'waze':
      if (platform === 'ios' || platform === 'android') {
        // Waze URL scheme (works on both iOS and Android)
        return `waze://?ll=${coords}&navigate=yes&q=${encodeURIComponent(query)}`;
      }
      // Web fallback
      return `https://waze.com/ul?ll=${coords}&navigate=yes`;

    case 'system':
      if (platform === 'ios') {
        // iOS system default (opens in Apple Maps)
        return `maps:///?daddr=${coords}&dirflg=d&q=${encodeURIComponent(query)}`;
      } else if (platform === 'android') {
        // Android system default (opens in Google Maps or user default)
        return `geo:${coords}?q=${encodeURIComponent(query)}`;
      }
      // Web fallback
      return `https://www.google.com/maps/dir/?api=1&destination=${coords}&travelmode=driving`;

    default:
      throw new Error(`Unsupported maps app: ${app}`);
  }
}

/**
 * Attempts to open a maps app with navigation to the destination
 */
export async function openExternalMapsApp(
  destination: NavigationDestination,
  app: MapsApp
): Promise<boolean> {
  const platform = getPlatform();

  try {
    // Check if app is available on this platform
    if (!isMapsAppAvailable(app, platform)) {
      toast.error(`${app} is not available on this platform`);
      return false;
    }

    const url = generateMapsUrl(destination, app, platform);

    // For mobile platforms, try URL schemes first
    if (platform === 'ios' || platform === 'android') {
      // Create a temporary link element to test URL scheme
      const link = document.createElement('a');
      link.href = url;
      link.style.display = 'none';
      document.body.appendChild(link);

      try {
        // Try to open the app
        window.location.href = url;

        // If we're still here after a short delay, the app didn't open
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Check if we're still on the same page (app didn't open)
        if (window.location.href.includes(url) || document.hasFocus()) {
          throw new Error('App did not open');
        }

        document.body.removeChild(link);
        return true;
      } catch (error) {
        document.body.removeChild(link);

        // If URL scheme failed, try web fallback
        if (app !== 'system') {
          const webUrl = generateMapsUrl(destination, 'system', 'web');
          window.open(webUrl, '_blank');
          toast.success('Opened navigation in web browser');
          return true;
        }

        throw error;
      }
    } else {
      // Web platform - open in new tab
      window.open(url, '_blank');
      toast.success('Opened navigation in new tab');
      return true;
    }
  } catch (error) {
    console.error('Failed to open maps app:', error);
    toast.error('Unable to open navigation. Please check your device settings.');
    return false;
  }
}

/**
 * Opens navigation based on user preferences with comprehensive error handling
 */
export async function openNavigationWithPreference(
  destination: NavigationDestination,
  navigationMode: 'internal' | 'external',
  defaultMapsApp?: MapsApp,
  onInternalNavigation?: (destination: NavigationDestination) => void
): Promise<{ success: boolean; method: 'internal' | 'external' | 'fallback' }> {
  // Validate destination
  if (!isValidDestination(destination)) {
    toast.error('Invalid destination coordinates');
    return { success: false, method: 'internal' };
  }

  if (navigationMode === 'internal') {
    // Use in-app navigation
    if (onInternalNavigation) {
      onInternalNavigation(destination);
      return { success: true, method: 'internal' };
    }
    toast.error('Internal navigation not available');
    return { success: false, method: 'internal' };
  } else {
    // Use external navigation
    if (!defaultMapsApp) {
      toast.error('No default maps app selected. Please configure in Settings > Navigation.');
      return { success: false, method: 'external' };
    }

    const success = await openExternalMapsApp(destination, defaultMapsApp);

    if (success) {
      return { success: true, method: 'external' };
    } else {
      // Try fallback to web version
      try {
        const webUrl = generateMapsUrl(destination, 'system', 'web');
        window.open(webUrl, '_blank');
        toast.success('Opened navigation in web browser');
        return { success: true, method: 'fallback' };
      } catch (error) {
        toast.error('Unable to open navigation. Please check your device settings.');
        return { success: false, method: 'external' };
      }
    }
  }
}

/**
 * Validates navigation destination coordinates
 */
export function isValidDestination(destination: NavigationDestination): boolean {
  const { latitude, longitude } = destination;

  return (
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    latitude >= -90 && latitude <= 90 &&
    longitude >= -180 && longitude <= 180 &&
    !isNaN(latitude) &&
    !isNaN(longitude)
  );
}